type CrmExcelValue =
  | string
  | number
  | null
  | undefined;

export interface CrmExcelColumn<T> {
  header: string;
  key: string;
  width: number;
  value: (row: T) => CrmExcelValue;
  numFmt?: string;
}

interface ExportCrmExcelOptions<T> {
  filename: string;
  sheetName: string;
  columns: Array<CrmExcelColumn<T>>;
  rows: T[];
}

export async function exportCrmExcel<T>(
  options: ExportCrmExcelOptions<T>
): Promise<void> {
  const ExcelJS = (await import("exceljs")).default;

  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Circuit Cover";
  workbook.created = new Date();

  const worksheet = workbook.addWorksheet(
    options.sheetName.slice(0, 31)
  );

  worksheet.columns = options.columns.map(
    ({ header, key, width }) => ({
      header,
      key,
      width,
    })
  );

  for (const sourceRow of options.rows) {
    const excelRow: Record<string, CrmExcelValue> = {};

    for (const column of options.columns) {
      excelRow[column.key] =
        column.value(sourceRow) ?? "";
    }

    worksheet.addRow(excelRow);
  }

  const headerRow = worksheet.getRow(1);
  headerRow.height = 24;

  headerRow.eachCell((cell) => {
    cell.font = {
      bold: true,
      color: { argb: "FFFFFFFF" },
    };

    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF1A3AB8" },
    };

    cell.alignment = {
      vertical: "middle",
      horizontal: "center",
    };
  });

  for (const column of options.columns) {
    if (column.numFmt) {
      worksheet.getColumn(column.key).numFmt =
        column.numFmt;
    }
  }

  worksheet.views = [
    {
      state: "frozen",
      ySplit: 1,
    },
  ];

  worksheet.autoFilter = {
    from: {
      row: 1,
      column: 1,
    },
    to: {
      row: 1,
      column: options.columns.length,
    },
  };

  const buffer = await workbook.xlsx.writeBuffer();

  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = options.filename;

  document.body.appendChild(link);
  link.click();
  link.remove();

  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 1000);
}